import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FITZPATRICK_TYPES } from "@/lib/constants";

// A human body temperature in °C is roughly 35-42; in °F roughly 95-106. Any
// reading at or above 45 can only be Fahrenheit. Same threshold the server-side
// backfill uses, so the two agree.
const looksFahrenheit = (t) => typeof t === "number" && t >= 45;
const toCelsius = (f) => Math.round(((f - 32) * 5 / 9) * 10) / 10;

function F({ label, children, span }) {
  return (
    <div className={`space-y-1 ${span || ""}`}>
      <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

export default function VitalsForm({ vitals, onChange, previousVitals }) {
  const v = vitals || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const setNum = (k, raw) => {
    const n = raw === "" ? null : Number(raw);
    set(k, isNaN(n) ? null : n);
  };

  const bmi = v.weight && v.height && v.height > 0
    ? (v.weight / ((v.height / 100) ** 2)).toFixed(1)
    : null;

  return (
    <div className="space-y-4">
      {previousVitals && (
        <div className="rounded-lg bg-cream/60 px-3 py-2">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Previous Visit</p>
          <p className="text-xs text-muted-foreground">
            {[
              previousVitals.bp_systolic && `BP: ${previousVitals.bp_systolic}/${previousVitals.bp_diastolic}`,
              previousVitals.weight && `Wt: ${previousVitals.weight}kg`,
              previousVitals.pulse_rate && `Pulse: ${previousVitals.pulse_rate}`,
              previousVitals.bmi && `BMI: ${previousVitals.bmi}`,
            ].filter(Boolean).join("  ·  ") || "No prior vitals"}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <F label="BP Systolic (mmHg)">
          <Input type="number" placeholder="120" value={v.bp_systolic ?? ""} onChange={(e) => setNum("bp_systolic", e.target.value)} />
        </F>
        <F label="BP Diastolic (mmHg)">
          <Input type="number" placeholder="80" value={v.bp_diastolic ?? ""} onChange={(e) => setNum("bp_diastolic", e.target.value)} />
        </F>
        <F label="Pulse Rate (bpm)">
          <Input type="number" placeholder="72" value={v.pulse_rate ?? ""} onChange={(e) => setNum("pulse_rate", e.target.value)} />
        </F>
        <F label="SpO2 (%)">
          <Input type="number" placeholder="98" value={v.spo2 ?? ""} onChange={(e) => setNum("spo2", e.target.value)} />
        </F>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <F label="Weight (kg)">
          <Input type="number" step="0.1" placeholder="70" value={v.weight ?? ""} onChange={(e) => setNum("weight", e.target.value)} />
        </F>
        <F label="Height (cm)">
          <Input type="number" placeholder="170" value={v.height ?? ""} onChange={(e) => setNum("height", e.target.value)} />
        </F>
        <F label="BMI (auto)">
          <Input readOnly value={bmi || "—"} className="bg-cream/40 font-semibold" />
        </F>
        <F label="Temperature (°C)">
          <Input type="number" step="0.1" placeholder="37.0" value={v.temperature ?? ""} onChange={(e) => setNum("temperature", e.target.value)} />
          {/* This field was labelled °F until recently, so muscle memory will
              keep producing 98.6. Catch it at the point of entry rather than
              letting a nonsensical reading reach the prescription. */}
          {looksFahrenheit(v.temperature) && (
            <p className="mt-1 text-xs font-medium text-amber-700">
              That looks like °F — {toCelsius(v.temperature)} °C?
            </p>
          )}
        </F>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <F label="Skin Type (Fitzpatrick)" span="col-span-2">
          <Select value={v.skin_type || ""} onValueChange={(val) => set("skin_type", val)}>
            <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              {FITZPATRICK_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </F>
      </div>

      <F label="Vitals Notes">
        <Textarea rows={2} placeholder="Additional observations..." value={v.vitals_notes || ""} onChange={(e) => set("vitals_notes", e.target.value)} />
      </F>
    </div>
  );
}
